import { useCallback, useEffect, useMemo, useRef } from 'react';
import { handleOverlayPopstate, provaARiavvolgereUnaVoce, runAfterProgrammaticPops } from './useOverlayBackButton';

/**
 * Logical "location" of the custom state-based router in App.tsx.
 * Fully determines what is rendered (screen + the param states it depends on).
 * Token-driven screens (verify-email/reset-password) are intentionally not
 * represented by their tokens — they are entered once on mount.
 */
export interface AppLocation {
  screen: string;
  profileUsername: string | null;
  /** Tab the profile screen opens on (null = its default). */
  profileTab: string | null;
  /** Voce della FAQ aperta all'arrivo (null = nessuna). */
  faqEntry: string | null;
  /**
   * Capitolo delle regole dei tornei mostrato all'arrivo (null = dall'inizio).
   *
   * Come `faqEntry`, e per la stessa ragione: `sameLocation` non lo confronta,
   * quindi due ingressi di fila sulla stessa pagina con ancore diverse non
   * spingono una seconda voce di cronologia. Tenerlo qui serve al ritorno
   * «indietro», che deve rimettere la pagina dove l'utente l'aveva lasciata.
   */
  tournamentAnchor: string | null;
  selectedArenaId: string | null;
  selectedTournamentId: string | null;
  selectedPuzzleId: number | null;
  /**
   * Partita aperta nello Storico (screen === 'games-history'). Sta qui perché
   * senza di lei tornare su quella posizione rimontava la schermata SENZA partita,
   * cioè l'elenco spoglio che nessuno aveva chiesto — al posto della review che
   * c'era prima.
   */
  replayGameId: string | null;
  /** Variant whose rules page is shown (screen === 'rules-variant'). */
  rulesVariant: string | null;
  /** True for the rules index page (screen === 'rules-index'). */
  rulesIndex: boolean;
}

export interface UseBackNavigationArgs {
  /** Current logical location (memoize in App with useMemo). */
  location: AppLocation;
  /** Fan-out a location into the App's state setters. */
  applyLocation: (loc: AppLocation) => void;
  /** True when `screen` is a game screen with a game currently in progress. */
  isGameInProgress: (screen: string) => boolean;
  /** Called when a back press is trapped on a live game — open the leave modal. */
  onGuardedBack: (screen: string) => void;
  /**
   * Map a logical location to the URL path it should occupy. Defaults to the
   * current href (i.e. every screen stays at `/`). Rules locations return their
   * deep `/lang/segment/slug` path so they are real, crawlable URLs.
   */
  locationToUrl?: (loc: AppLocation) => string | undefined;
}

export interface BackNavigationApi {
  /**
   * Mark the next forward navigation as a *replace* instead of a push: the
   * current history entry is overwritten rather than a new one added. Use when
   * leaving a game so a later back press doesn't return to the (now abandoned)
   * game screen. Call immediately before the setScreen that performs the leave.
   */
  navigateReplace: () => void;
  /**
   * Il pulsante «indietro» DENTRO l'app: fa quello che fa la freccia del
   * browser, cosi' le due strade portano nello stesso posto — la schermata di
   * prima, con i suoi parametri (il torneo, la variante, la partita aperta).
   *
   * Serve dove la freccia del browser non c'e': l'app installata su iPhone.
   * Senza una schermata precedente creata dall'app (il profilo aperto da un
   * link, dopo un riavvio) il gesto uscirebbe dall'app: allora `ripiego`.
   */
  tornaIndietro: (ripiego: () => void) => void;
}

function sameLocation(a: AppLocation, b: AppLocation): boolean {
  return (
    a.screen === b.screen &&
    a.profileUsername === b.profileUsername &&
    a.profileTab === b.profileTab &&
    a.selectedArenaId === b.selectedArenaId &&
    a.selectedTournamentId === b.selectedTournamentId &&
    a.selectedPuzzleId === b.selectedPuzzleId &&
    a.rulesVariant === b.rulesVariant &&
    a.rulesIndex === b.rulesIndex
  );
}

interface HistoryIdxState {
  idx: number;
}

function readIdx(state: unknown): number | null {
  if (state && typeof state === 'object' && typeof (state as HistoryIdxState).idx === 'number') {
    return (state as HistoryIdxState).idx;
  }
  return null;
}

/**
 * Integrates the browser History API with App.tsx's state-based router so that
 * the back gesture / back button navigates *within* the app instead of exiting
 * the PWA, and is trapped (with a confirmation) while a game is in progress.
 *
 * Strategy (effect-based, non-invasive — no setScreen call site changes):
 *  - We own an in-memory stack mirroring the history entries this app created.
 *    Entry n carries `history.state = { idx: n }`; that idx — not history.length,
 *    which is unreliable across PWA cold-starts — is the source of truth.
 *  - An observer effect pushes a new history entry on each forward navigation.
 *  - A popstate listener decides: exit at the root, trap on a live game, or
 *    re-apply the previous location for an ordinary in-app back.
 *  - `isPoppingRef` prevents the back-induced state change from being re-pushed.
 */
export function useBackNavigation({
  location,
  applyLocation,
  isGameInProgress,
  onGuardedBack,
  locationToUrl,
}: UseBackNavigationArgs): BackNavigationApi {
  const stackRef = useRef<AppLocation[]>([]);
  const isPoppingRef = useRef(false);
  const replaceNextRef = useRef(false);

  // Resolve the URL a location should occupy. Defaults to the current href so
  // non-rules screens keep `/` exactly as before.
  const urlFor = (loc: AppLocation): string =>
    (locationToUrl ? locationToUrl(loc) : undefined) ?? window.location.href;

  // Keep latest callbacks/location reachable from the popstate handler without
  // re-registering the listener on every render.
  const cbRef = useRef({ applyLocation, isGameInProgress, onGuardedBack });
  cbRef.current = { applyLocation, isGameInProgress, onGuardedBack };

  // Seed the base entry and register the popstate listener once on mount.
  useEffect(() => {
    stackRef.current = [location];
    // Anchor the base entry's idx while preserving the current URL.
    window.history.replaceState({ idx: 0 }, '', window.location.href);

    const onPop = (event: PopStateEvent) => {
      // Overlays own the back gesture first: while a modal / full-screen panel
      // is open, back closes it instead of navigating (or trapping on a game).
      if (handleOverlayPopstate()) return;

      const stack = stackRef.current;
      const currentIdx = stack.length - 1;
      const current = stack[currentIdx];

      // Trap FIRST, regardless of the target idx: while a game is in progress
      // the back gesture must never leave. Re-push to cancel the navigation and
      // ask for confirmation. Checked before the idx arithmetic so that rapid
      // repeated back presses are each trapped (every press re-pushes a buffer
      // entry, so there is always something to pop on the next press).
      if (current && cbRef.current.isGameInProgress(current.screen)) {
        window.history.pushState({ idx: currentIdx }, '', window.location.href);
        cbRef.current.onGuardedBack(current.screen);
        return;
      }

      const targetIdx = readIdx(event.state) ?? currentIdx - 1;

      // Forward (unused) — only "back" is handled here.
      if (targetIdx >= currentIdx) return;

      // Below the base entry → let the browser do its default (exit the app).
      if (targetIdx < 0) return;

      // Ordinary in-app back: re-apply the previous location and truncate.
      const target = stack[targetIdx];
      if (!target) return;
      stackRef.current = stack.slice(0, targetIdx + 1);
      isPoppingRef.current = true;
      cbRef.current.applyLocation(target);
    };

    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Observer: push a history entry on each distinct forward navigation.
  useEffect(() => {
    if (isPoppingRef.current) {
      isPoppingRef.current = false;
      return;
    }
    const top = stackRef.current[stackRef.current.length - 1];

    // Replace the current entry instead of pushing (e.g. leaving a game), so a
    // later back press doesn't land on the abandoned game screen. La bandierina
    // si consuma SUBITO, non dentro il lavoro rimandato qui sotto: e' alzata per
    // questa navigazione, e lasciarla su fino allo scarico la farebbe valere per
    // quella dopo.
    const replace = replaceNextRef.current;
    replaceNextRef.current = false;

    // No real change (e.g. token-screen replaceState, or repeated render).
    if (!replace && top && sameLocation(top, location)) return;

    /*
     * La scrittura aspetta che gli overlay che si stanno chiudendo abbiano
     * finito di riavvolgere la cronologia.
     *
     * Una schermata raggiunta DA un overlay — avviare una partita dal foglio
     * "Partita personalizzata", aprire le tabelle di apertura — chiude uno o due
     * overlay e cambia posizione nello stesso commit. Le chiusure programmatiche
     * chiamano `history.back()`, che e' asincrono; scrivere qui e adesso infilava
     * la voce nuova prima che quei back arrivassero, e siccome `pushState` tronca
     * le voci in avanti se la mangiavano — lasciando il cursore SOTTO la posizione
     * logica dell'app. Il primo «indietro» usciva dall'app.
     *
     * Senza chiusure in volo `runAfterProgrammaticPops` esegue subito, quindi per
     * ogni navigazione ordinaria non cambia niente.
     */
    runAfterProgrammaticPops(() => {
      const stack = stackRef.current;
      if (replace) {
        const idx = Math.max(0, stack.length - 1);
        /*
         * VOCI GEMELLE: una pagina, una voce.
         *
         * Sostituire scrive la posizione nuova SOPRA quella corrente, senza
         * guardare quella sotto — e quando le due coincidono resta una coppia di
         * voci per la stessa pagina, cioe' una pressione dell'indietro in cui a
         * schermo non cambia niente. Le coniava ogni turno di torneo: dalla
         * scheda l'accoppiamento SPINGE la damiera, e «Continua» la sostituisce
         * con la scheda da cui si era partiti. Uno svizzero di cinque turni
         * lasciava cinque gemelle, e dalla home ci volevano otto pressioni per
         * tornare all'elenco, sei delle quali non cambiavano nulla: era la
         * segnalazione «dal torneo finito l'indietro mi riporta sempre li'».
         * Stessa moneta per l'arena a ogni partita e per l'uscita dall'invito
         * ai problemi: la regola sta qui perche' e' di tutti e tre.
         *
         * Collassare vuol dire TORNARE a quella voce, non riscriverne una: la
         * cronologia non sa togliere una voce, sa solo muovere il cursore. Il
         * `back()` parte da qui — da un cambio di stato — e non dal gestore
         * `popstate`, dove sarebbe annidato in una traversata: e' la famiglia di
         * guasti che `useOverlayBackButton` documenta per esteso. Se non e'
         * sicuro riavvolgere (un overlay aperto, un riavvolgimento in volo) la
         * funzione dice `false` e si scrive la gemella come prima: degrado, non
         * guasto.
         *
         * Lo specchio si tronca PRIMA del `back()`, e cosi' il `popstate` che
         * segue e' un no-op per due vie indipendenti: lo ingoia il contatore
         * delle chiusure programmatiche, e se anche arrivasse fin qui il gestore
         * leggerebbe `targetIdx === currentIdx` e uscirebbe dal ramo «in avanti»
         * (riga 167). Il cursore resta su una voce il cui `idx` e il cui
         * indirizzo sono gia' quelli giusti: non c'e' niente da riscrivere.
         */
        const sotto = idx > 0 ? stack[idx - 1] : undefined;
        if (sotto && sameLocation(sotto, location) && provaARiavvolgereUnaVoce()) {
          stackRef.current = stack.slice(0, idx);
          return;
        }
        stackRef.current = [...stack.slice(0, idx), location];
        window.history.replaceState({ idx }, '', urlFor(location));
        return;
      }
      const nextIdx = stack.length;
      stackRef.current = [...stack, location];
      window.history.pushState({ idx: nextIdx }, '', urlFor(location));
    });
  }, [location]);

  /*
   * Memoizzata, e l'oggetto che la porta con lei. Non e' una pulizia di forma:
   * `navigateReplace` sta negli array di dipendenza di chi la usa
   * (`App.tsx:1015`, `:1091`, `:1100`), e con un'identita' nuova a ogni render
   * quegli array erano inerti — l'effetto dell'instradamento dell'arena, che
   * NAVIGA, girava a ogni render di `App` e il suo elenco di dipendenze non
   * diceva la verita' su quando dovesse girare. Non tocca nessun ref: la
   * funzione alza solo una bandierina che l'osservatore qui sopra consuma al
   * giro dopo.
   */
  const navigateReplace = useCallback(() => {
    replaceNextRef.current = true;
  }, []);

  /*
   * `history.back()` e non `applyLocation` diretto: il ritorno deve passare dal
   * gestore di `popstate` come quello del browser, che tronca la pila e non
   * riscrive la voce. Applicando la posizione a mano la pila crescerebbe di una
   * voce, e la freccia del browser dopo il pulsante riporterebbe al profilo.
   */
  const tornaIndietro = useCallback((ripiego: () => void) => {
    if (stackRef.current.length > 1) window.history.back();
    else ripiego();
  }, []);

  return useMemo(() => ({ navigateReplace, tornaIndietro }), [navigateReplace, tornaIndietro]);
}
